import type { ReactNode } from 'react';
import { CalendarMonthOutlined, ChatBubbleOutlineRounded, GroupsOutlined, PublicOutlined, WbSunnyOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { InformationPage, PageSections, designTokens } from '../../../design-system';
import type { IntegrationSettingsRoute } from '../model/types';
import { IntegrationNavigationList } from './IntegrationNavigationList';
import { GoogleWorkspaceMark, SlackMark } from './ProviderMarks';

function Services({ children, label }: { children: ReactNode; label: string }) {
  return <Stack direction="row" spacing={1} useFlexGap sx={{
    alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, flexWrap: 'wrap',
    color: designTokens.color.secondaryText,
  }}>
    <Stack direction="row" spacing={0.5} aria-hidden sx={{ alignItems: 'center' }}>{children}</Stack>
    <Typography component="span" variant="caption">{label}</Typography>
  </Stack>;
}

export function IntegrationOverview({ onOpen, onOpenHolidayCalendars }: {
  onOpen: (route: IntegrationSettingsRoute) => void;
  onOpenHolidayCalendars?: () => void;
}) {
  return (
    <InformationPage title="연동" width="compact" testId="integration-settings-overview">
      <PageSections>
        <IntegrationNavigationList label="업무별 연동" items={[
          {
            label: '디렉터리 동기화',
            description: '구성원 프로필과 조직 정보를 최신 상태로 유지합니다.',
            icon: <GroupsOutlined color="action" />,
            value: <Services label="Google Workspace · Slack"><GoogleWorkspaceMark size={16} /><SlackMark size={16} /></Services>,
            onOpen: () => onOpen('directory'),
          },
          {
            label: '휴가·부재 동기화',
            description: '확정 휴가를 캘린더 일정과 메신저 상태에 반영합니다.',
            icon: <CalendarMonthOutlined color="action" />,
            value: <Services label="Google Calendar · Slack"><CalendarMonthOutlined sx={{ fontSize: 16 }} /><SlackMark size={16} /></Services>,
            onOpen: () => onOpen('absence-sync'),
          },
          {
            label: '알림·메신저 앱',
            description: '업무 소식을 받고 구성원과 휴가 정보를 조회합니다.',
            icon: <ChatBubbleOutlineRounded color="action" />,
            value: <Services label="Slack"><SlackMark size={16} /></Services>,
            onOpen: () => onOpen('messaging'),
          },
          {
            label: '공휴일 가져오기',
            description: onOpenHolidayCalendars
              ? '대한민국 공휴일을 휴일 캘린더에 반영합니다.'
              : '휴일 캘린더 조회 권한이 필요합니다.',
            icon: <WbSunnyOutlined color="action" />,
            value: <Services label="한국천문연구원"><PublicOutlined sx={{ fontSize: 16 }} /></Services>,
            onOpen: () => onOpenHolidayCalendars?.(),
            disabled: !onOpenHolidayCalendars,
          },
        ]} />
      </PageSections>
    </InformationPage>
  );
}
